/**
 * <slider-dots> — generic pagination dots for a horizontally-scrolling slider.
 *
 * Renders one dot per "page" (a viewport-width step) of a scroll container and
 * keeps the active dot in sync with the scroll position; clicking a dot scrolls
 * to that page. Engine-agnostic — it just observes a scroll element, so it works
 * with the section mobile slider or any scroll-snap row.
 *
 * Attributes:
 *   data-scroller — CSS selector for the scroll container. Resolved against the
 *                   previous element sibling first, then the closest section.
 *
 * No-op when the scroller can't be resolved or doesn't overflow (single page).
 */
class SliderDots extends HTMLElement {
  /** @type {HTMLElement | null} */
  #scroller = null;

  /** @type {HTMLButtonElement[]} */
  #dots = [];

  /** @type {ResizeObserver | null} */
  #resizeObserver = null;

  connectedCallback() {
    const selector = this.dataset.scroller;
    if (!selector) return;

    const prev = this.previousElementSibling;
    let scroller =
      prev instanceof HTMLElement && prev.matches(selector) ? prev : null;
    if (!scroller) {
      const scope = this.closest('.section') || this.parentElement || document;
      const found = scope.querySelector(selector);
      if (found instanceof HTMLElement) scroller = found;
    }
    if (!scroller) return;

    this.#scroller = scroller;
    this.#render();

    scroller.addEventListener('scroll', this.#update, { passive: true });
    this.#resizeObserver = new ResizeObserver(this.#render);
    this.#resizeObserver.observe(scroller);
  }

  disconnectedCallback() {
    this.#scroller?.removeEventListener('scroll', this.#update);
    this.#resizeObserver?.disconnect();
  }

  #pageCount() {
    const scroller = this.#scroller;
    if (!scroller || scroller.clientWidth === 0) return 0;

    // Prefer a deterministic count from the number of slides and how many are
    // shown per view: pages = ceil(slides / perView). This is exact and immune
    // to the sub-pixel/gap/padding rounding that made the width-ratio approach
    // (Math.round(scrollWidth / clientWidth)) drop the last page — e.g. 3 items
    // at 1-per-view reported 2 pages instead of 3.
    const slideCount = scroller.childElementCount;
    const perViewRaw = getComputedStyle(scroller).getPropertyValue('--mobile-slides-per-view');
    const perView = parseFloat(perViewRaw);
    if (slideCount > 0 && Number.isFinite(perView) && perView >= 1) {
      return Math.max(1, Math.ceil(slideCount / perView));
    }

    // Fallback: derive from the scroll extent when the per-view var is absent.
    return Math.max(1, Math.ceil((scroller.scrollWidth - 1) / scroller.clientWidth));
  }

  #render = () => {
    const scroller = this.#scroller;
    if (!scroller) return;

    const pages = this.#pageCount();

    // Single page → nothing to paginate.
    if (pages <= 1) {
      this.replaceChildren();
      this.#dots = [];
      return;
    }

    if (this.#dots.length !== pages) {
      const list = document.createElement('ol');
      list.className = 'carousel-controls carousel-controls--dots carousel-controls__dots';
      this.#dots = [];

      for (let i = 0; i < pages; i++) {
        const li = document.createElement('li');
        const btn = document.createElement('button');
        btn.type = 'button';
        btn.className = 'carousel-controls__dot';
        btn.setAttribute('aria-label', String(i + 1));
        btn.addEventListener('click', () => {
          scroller.scrollTo({ left: i * scroller.clientWidth, behavior: 'smooth' });
        });
        li.appendChild(btn);
        list.appendChild(li);
        this.#dots.push(btn);
      }

      this.replaceChildren(list);
    }

    this.#update();
  };

  #update = () => {
    const scroller = this.#scroller;
    if (!scroller || this.#dots.length === 0) return;

    const active = Math.round(scroller.scrollLeft / scroller.clientWidth);
    this.#dots.forEach((dot, i) => dot.setAttribute('aria-selected', String(i === active)));
  };
}

if (!customElements.get('slider-dots')) {
  customElements.define('slider-dots', SliderDots);
}
